
import React, { useState } from 'react';
import { useApiLogs } from '@/hooks/useApiIntegrations';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Loader2, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface LogsViewerProps {
  userId?: string;
  providerId?: string;
  limit?: number;
}

export function LogsViewer({ userId, providerId, limit = 50 }: LogsViewerProps) {
  const { data: logs = [], isLoading } = useApiLogs(userId, limit);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  
  const filteredLogs = providerId 
    ? logs.filter(log => log.provider_id === providerId)
    : logs;
  
  const handleShowDetails = (log: any) => {
    setSelectedLog(log);
  };
  
  const handleCloseDetails = () => {
    setSelectedLog(null);
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-amber-500" />;
      default:
        return null;
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  
  if (filteredLogs.length === 0) {
    return (
      <Card className="border border-dashed">
        <CardHeader>
          <CardTitle>No API Logs</CardTitle>
          <CardDescription>
            No API requests have been logged yet.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Provider</TableHead>
            <TableHead>Endpoint</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredLogs.map((log) => {
            // Calculate duration if both timestamps are available
            let duration = null;
            if (log.started_at && log.completed_at) {
              const started = new Date(log.started_at);
              const completed = new Date(log.completed_at);
              duration = (completed.getTime() - started.getTime()) / 1000; // in seconds
            }
            
            return (
              <TableRow 
                key={log.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleShowDetails(log)}
              >
                <TableCell className="whitespace-nowrap">
                  {log.started_at ? format(new Date(log.started_at), 'MMM d, HH:mm:ss') : 'N/A'}
                </TableCell>
                <TableCell>
                  {log.provider?.name || 'Unknown'}
                </TableCell>
                <TableCell className="font-mono text-sm max-w-[200px] truncate">
                  {log.endpoint}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(log.status)}
                    <Badge
                      variant={
                        log.status === 'success' ? 'default' :
                        log.status === 'error' ? 'destructive' : 'outline'
                      }
                    >
                      {log.status}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {duration !== null ? `${duration.toFixed(2)}s` : 'N/A'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      <Dialog open={!!selectedLog} onOpenChange={handleCloseDetails}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>API Log Details</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Provider</h4>
                  <p>{selectedLog.provider?.name || 'Unknown'}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Status</h4>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(selectedLog.status)}
                    <Badge
                      variant={
                        selectedLog.status === 'success' ? 'default' :
                        selectedLog.status === 'error' ? 'destructive' : 'outline'
                      }
                    >
                      {selectedLog.status}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-1">Endpoint</h4>
                <p className="font-mono text-sm bg-muted p-2 rounded">{selectedLog.endpoint}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Started At</h4>
                  <p>
                    {selectedLog.started_at 
                      ? format(new Date(selectedLog.started_at), 'PPpp')
                      : 'N/A'
                    }
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Completed At</h4>
                  <p>
                    {selectedLog.completed_at 
                      ? format(new Date(selectedLog.completed_at), 'PPpp') 
                      : 'N/A'
                    }
                  </p>
                </div>
              </div>
              
              {selectedLog.items_synced !== null && selectedLog.items_synced !== undefined && (
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Items Synced</h4>
                  <p>{selectedLog.items_synced}</p>
                </div>
              )}
              
              {selectedLog.error_message && (
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">Error Message</h4>
                  <div className="bg-destructive/10 border border-destructive/20 rounded p-2">
                    <p className="text-sm font-mono text-destructive whitespace-pre-wrap">
                      {selectedLog.error_message}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
